import { log } from './log'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// Initialized at top-level to avoid TDZ in production bundles.
// Real ESPN abbreviations: RD16=R1, QTR=Semis, SEMI=Conf Finals, FINAL=Finals
const PLAYOFF_ABBRS = new Set(['RD16','QTR','SEMI','FINAL','WC','DS','CS','WS','SB','DIV','CONF'])
// Per-sport whitelist. Used as a stricter filter inside buildBracketFromGames
// so an NHL bracket can never accidentally include an NFL-tagged event.
const SPORT_ABBRS = {
  NBA: new Set(['RD16','QTR','SEMI','FINAL']),
  NHL: new Set(['RD16','QTR','SEMI','FINAL']),
  MLB: new Set(['WC','DS','CS','WS']),
  NFL: new Set(['WC','DIV','CONF','SB']),
}
// STD/REG are regular-season tags that can sneak through the playoff endpoint
const IGNORE_ABBRS  = new Set(['STD','REG',''])

const PLAYOFF_MONTHS = {
  NBA: [3, 4, 5],
  NHL: [3, 4, 5],
  MLB: [9, 10],
  NFL: [0, 1],
}

export function isPlayoffSeason(sport) {
  const m = new Date().getMonth()
  return PLAYOFF_MONTHS[sport]?.includes(m) ?? false
}

const SPORT_PATHS = {
  NBA: 'basketball/nba',
  NHL: 'hockey/nhl',
  MLB: 'baseball/mlb',
  NFL: 'football/nfl',
}

// ── Pure ESPN data — no cascade logic ────────────────────────────────────────
// ESPN's allplayoffs endpoint contains all round games. We filter to known
// playoff abbreviations, drop regular-season tags (STD/REG), and group into
// series. If a later round has no data yet it simply won't appear — correct.
function buildBracketFromGames(events, sport) {
  // Drop non-playoff events. Sport-specific allow-list catches STD/REG plus
  // any other-sport abbreviation that could leak through if ESPN ever mixed
  // payloads. Falls back to the cross-sport playoff set when sport is
  // unknown, so the function still works for unmapped sports.
  const sportAbbrs    = SPORT_ABBRS[sport]
  const playoffEvents = events.filter(e => {
    const abbr = e.competitions?.[0]?.type?.abbreviation
    if (!abbr || IGNORE_ABBRS.has(abbr)) return false
    return sportAbbrs ? sportAbbrs.has(abbr) : PLAYOFF_ABBRS.has(abbr)
  })
  log('[playoffs] events after abbr filter:', playoffEvents.length,
    'of', events.length, '| abbrs:', [...new Set(playoffEvents.map(e => e.competitions?.[0]?.type?.abbreviation))])

  // Step 1: Group playoff events into series by round
  const seriesByRound = {}

  // Sibling map of display metadata keyed by team displayName. Same ESPN
  // payload that supplies the team names also carries color/alternateColor/
  // abbreviation/shortDisplayName; capture them once per team here so the
  // bracket UI's TeamLogoImg call sites can look them up without changing
  // the series-object shape (team1/team2 stay strings — pickKey, picks Map,
  // migration, and name-equality reads are all unaffected).
  const teamColors = {}

  playoffEvents.forEach(event => {
    const comp = event.competitions?.[0]
    const home = comp?.competitors?.find(c => c.homeAway === 'home')
    const away = comp?.competitors?.find(c => c.homeAway === 'away')
    if (!home || !away) return

    const homeTeam = home.team?.displayName || ''
    const awayTeam = away.team?.displayName || ''
    // ESPN sometimes ships pre-event slots with displayName='TBD' for
    // upcoming rounds. Treat 'TBD' the same as missing — skip the event so
    // the series-key generator below can't produce IDs like 'TBD|TBD|FINAL'
    // that look like real series but are unstable across reloads. Step 5
    // below fills any uncovered round slot with proper *_placeholder_*
    // matchups instead.
    if (!homeTeam || !awayTeam || homeTeam === 'TBD' || awayTeam === 'TBD') return

    // Capture display metadata for each team (idempotent — first capture wins;
    // later events for the same team don't overwrite). Display fields only —
    // does NOT include score/winner/anything per-game.
    if (!teamColors[homeTeam]) {
      teamColors[homeTeam] = {
        color:            home.team?.color            || null,
        alternateColor:   home.team?.alternateColor   || null,
        abbreviation:     home.team?.abbreviation     || null,
        shortDisplayName: home.team?.shortDisplayName || null,
      }
    }
    if (!teamColors[awayTeam]) {
      teamColors[awayTeam] = {
        color:            away.team?.color            || null,
        alternateColor:   away.team?.alternateColor   || null,
        abbreviation:     away.team?.abbreviation     || null,
        shortDisplayName: away.team?.shortDisplayName || null,
      }
    }

    const roundAbbr = comp?.type?.abbreviation || ''
    if (!roundAbbr) return

    // Sorted key so home/away flip between games maps to the same series
    const teams = [homeTeam, awayTeam].sort()
    const seriesKey = `${teams[0]}|${teams[1]}|${roundAbbr}`

    if (!seriesByRound[seriesKey]) {
      seriesByRound[seriesKey] = {
        id: seriesKey,
        team1: homeTeam,
        team2: awayTeam,
        seed1: home.seed || null,
        seed2: away.seed || null,
        wins1: 0,
        wins2: 0,
        winner: null,
        started: true,
        gamesPlayed: 0,
        roundAbbr,
        // Earliest scheduled commenceTime among this series' games, as an
        // ISO string. ISO strings sort lexicographically = chronologically,
        // so `<` comparison is safe without Date construction. Build A's
        // getBracketLockout(sport) reads this to decide R1/R2 tip-off.
        firstGameDate: event.date || null,
      }
    }

    const series = seriesByRound[seriesKey]
    series.gamesPlayed++
    // Running min: if a later event in the same series is scheduled earlier
    // (ESPN response order isn't guaranteed by date), capture the earliest.
    if (event.date && (!series.firstGameDate || event.date < series.firstGameDate)) {
      series.firstGameDate = event.date
    }

    // Count wins from completed games
    const isCompleted = comp?.status?.type?.completed
    if (isCompleted) {
      const homeScore = parseInt(home.score || 0)
      const awayScore = parseInt(away.score || 0)
      if (homeScore === 0 && awayScore === 0) return

      const gameWinner = homeScore > awayScore ? homeTeam : awayTeam

      if (gameWinner === series.team1) series.wins1++
      else series.wins2++

      const winsNeeded = sport === 'NFL' ? 1 : 4
      if (series.wins1 >= winsNeeded) series.winner = series.team1
      else if (series.wins2 >= winsNeeded) series.winner = series.team2
    }
  })

  // Step 2: Map round abbreviations to round info
  const ROUND_INFO = {
    'RD16':  { order: 1, name: 'First Round',          points: 1 },
    'QTR':   { order: 2, name: 'Semifinals',            points: 2 },
    'SEMI':  { order: 3, name: 'Conf Finals',           points: 4 },
    'FINAL': { order: 4, name: 'Finals',                points: 8 },
    'WC':    { order: 1, name: 'Wild Card',             points: 1 },
    'DS':    { order: 2, name: 'Division Series',       points: 2 },
    'CS':    { order: 3, name: 'Championship Series',   points: 4 },
    'WS':    { order: 4, name: 'World Series',          points: 8 },
    'DIV':   { order: 2, name: 'Divisional',            points: 2 },
    'CONF':  { order: 3, name: 'Conf Championship',     points: 4 },
    'SB':    { order: 4, name: 'Super Bowl',            points: 8 },
  }

  // Step 3: Group series into rounds
  const roundsMap = {}

  Object.values(seriesByRound).forEach(series => {
    const info = ROUND_INFO[series.roundAbbr]
    if (!info) return

    const order = info.order
    if (!roundsMap[order]) {
      roundsMap[order] = { name: info.name, points: info.points, matchups: [] }
    }

    roundsMap[order].matchups.push({
      ...series,
      roundName: info.name,
      roundPoints: info.points,
      roundOrder: order,
    })
  })

  // Step 4: Build rounds array sorted by order
  const rounds = Object.keys(roundsMap)
    .sort((a, b) => parseInt(a) - parseInt(b))
    .map(key => roundsMap[key])

  if (!rounds.length) return null

  // Step 5: Fill in placeholder rounds for any sport-standard rounds ESPN hasn't
  // published games for yet (e.g. Conf Finals / Finals not started).
  const ALL_ROUNDS = sport === 'MLB'
    ? [
        { order: 1, abbr: 'WC',    name: 'Wild Card',           points: 1 },
        { order: 2, abbr: 'DS',    name: 'Division Series',     points: 2 },
        { order: 3, abbr: 'CS',    name: 'Championship Series', points: 4 },
        { order: 4, abbr: 'WS',    name: 'World Series',        points: 8 },
      ]
    : sport === 'NFL'
    ? [
        { order: 2, abbr: 'DIV',   name: 'Divisional',          points: 2 },
        { order: 3, abbr: 'CONF',  name: 'Conf Championship',   points: 4 },
        { order: 4, abbr: 'SB',    name: 'Super Bowl',          points: 8 },
      ]
    : [  // NBA / NHL
        { order: 1, abbr: 'RD16',  name: 'First Round',         points: 1 },
        { order: 2, abbr: 'QTR',   name: 'Semifinals',          points: 2 },
        { order: 3, abbr: 'SEMI',  name: 'Conf Finals',         points: 4 },
        { order: 4, abbr: 'FINAL', name: 'Finals',              points: 8 },
      ]

  const placeholder = (roundDef, idx) => ({
    id: `${roundDef.abbr}_placeholder_${idx}`,
    team1: 'TBD', team2: 'TBD',
    seed1: null, seed2: null,
    wins1: 0, wins2: 0,
    winner: null, started: false, gamesPlayed: 0,
    roundAbbr: roundDef.abbr,
    roundName: roundDef.name,
    roundPoints: roundDef.points,
    roundOrder: roundDef.order,
  })

  ALL_ROUNDS.forEach(roundDef => {
    const matchupCount = Math.pow(2, 4 - roundDef.order)
    const existing     = rounds.find(r => r.matchups.some(m => m.roundAbbr === roundDef.abbr))
    if (!existing) {
      // Round absent entirely — create with all placeholders.
      rounds.push({
        name: roundDef.name,
        points: roundDef.points,
        matchups: Array.from({ length: matchupCount }, (_, i) => placeholder(roundDef, i)),
      })
    } else if (existing.matchups.length < matchupCount) {
      // Round partially published (e.g. East CF series exists, West CF hasn't
      // been added by ESPN yet). Pad with TBD placeholders so all expected
      // slots render. Winner-propagation below will fill them from prev-round.
      const need = matchupCount - existing.matchups.length
      for (let i = 0; i < need; i++) {
        existing.matchups.push(placeholder(roundDef, existing.matchups.length))
      }
    }
  })

  // Re-sort after adding placeholders
  rounds.sort((a, b) => (a.matchups[0]?.roundOrder || 0) - (b.matchups[0]?.roundOrder || 0))

  // Step 6: Propagate ESPN-confirmed winners into TBD slots of the next round.
  //
  // The earlier wi-advances-per-slot approach assumed ESPN ships rounds in
  // canonical seeding order, so the real-team slots aligned with the same
  // pair index their prev-round winners came from. When ESPN ships e.g.
  // West-CF-first during a partial publish, the placeholder East-CF slot
  // got filled with the West winners again — Knights appeared twice.
  //
  // Pair-based dedup instead: prev-round winners feed next-round matchups in
  // pairs (idx 0-1 → slot 0, 2-3 → slot 1, ...). If either team in a pair
  // is already placed in this round (i.e. ESPN already published the matchup
  // that pair feeds), the pair is "consumed" — skip it. Remaining unclaimed
  // pairs flow into placeholder slots in order.
  for (let i = 1; i < rounds.length; i++) {
    const prevWinners = rounds[i - 1].matchups.map(m => m.winner || null)

    const alreadyPlaced = new Set()
    rounds[i].matchups.forEach(m => {
      if (m.team1 && m.team1 !== 'TBD') alreadyPlaced.add(m.team1)
      if (m.team2 && m.team2 !== 'TBD') alreadyPlaced.add(m.team2)
    })

    const unclaimedPairs = []
    for (let pi = 0; pi < prevWinners.length; pi += 2) {
      const a = prevWinners[pi]
      const b = prevWinners[pi + 1]
      const aPlaced = a && alreadyPlaced.has(a)
      const bPlaced = b && alreadyPlaced.has(b)
      if (!aPlaced && !bPlaced) unclaimedPairs.push([a, b])
    }

    let pairIdx = 0
    rounds[i].matchups.forEach(matchup => {
      if (matchup.team1 === 'TBD' && matchup.team2 === 'TBD') {
        const pair = unclaimedPairs[pairIdx++]
        if (pair) {
          if (pair[0]) matchup.team1 = pair[0]
          if (pair[1]) matchup.team2 = pair[1]
        }
      }
    })
  }

  // Step 7: Canonicalize matchup order within each round so slot indices
  // align with the bracket tree structure (R1 slot 2k/2k+1 feed R2 slot k,
  // R2 slot 2k/2k+1 feed CF slot k, etc.). ESPN ships NHL R1/R2 in
  // interleaved conference order, but BracketTree pairs prev[2k]/prev[2k+1]
  // → next[k] for connectors and pairing visuals. Anchor on CF (and Finals)
  // — reorder R2 so its pairs match CF's slot pairing, then reorder R1 to
  // match the (now canonical) R2 ordering.
  //
  // Slot indices are assigned AFTER canonicalization so they're stable
  // across reloads regardless of ESPN's publish order. v2 picks key on
  // {round, slot} — stability here is foundational.
  const norm = (s) => (s || '').toString().trim().toLowerCase()
  const seriesMatches = (series, team) => {
    if (!team || team === 'TBD' || !series) return false
    const t = norm(team)
    return norm(series.team1) === t || norm(series.team2) === t
  }
  const canonicalize = (from, to) => {
    if (!from || !to) return from
    if (from.length < to.length * 2) return from
    const reordered = new Array(from.length).fill(null)
    const used = new Set()
    to.forEach((target, k) => {
      if (!target) return
      const i1 = from.findIndex((s, i) => !used.has(i) && seriesMatches(s, target.team1))
      if (i1 !== -1) { reordered[2 * k]     = from[i1]; used.add(i1) }
      const i2 = from.findIndex((s, i) => !used.has(i) && seriesMatches(s, target.team2))
      if (i2 !== -1) { reordered[2 * k + 1] = from[i2]; used.add(i2) }
    })
    let cursor = 0
    for (let i = 0; i < reordered.length; i++) {
      if (reordered[i] !== null) continue
      while (cursor < from.length && used.has(cursor)) cursor++
      if (cursor >= from.length) break
      reordered[i] = from[cursor]
      used.add(cursor)
      cursor++
    }
    if (reordered.some((s) => s === null)) return from
    return reordered
  }

  // Anchor on CF, propagate canonical order back to R2, then back to R1.
  // Run only when both anchor rounds exist (NBA/NHL have 4 rounds; NFL has 3).
  if (rounds[2] && rounds[1]) {
    rounds[1].matchups = canonicalize(rounds[1].matchups, rounds[2].matchups)
  }
  if (rounds[1] && rounds[0]) {
    rounds[0].matchups = canonicalize(rounds[0].matchups, rounds[1].matchups)
  }

  // Step 8: Assign stable slot indices to every matchup, post-canonicalization.
  // v2 picks key on {round, slot}; this is the contract.
  rounds.forEach(round => {
    round.matchups.forEach((m, idx) => { m.slot = idx })
  })

  log('[playoffs] rounds built:',
    rounds.map(r => `${r.name}: ${r.matchups.length} series`).join(', ')
  )

  return {
    sport,
    season: new Date().getFullYear(),
    rounds,
    finals: rounds[rounds.length - 1]?.matchups?.[0] || null,
    isLive: true,
    teamColors,
  }
}

export async function fetchPlayoffBracket(sport) {
  if (!SPORT_PATHS[sport]) return null
  const sportKey = sport.toLowerCase()

  // 10s timeout. If the server proxy / upstream ESPN hangs, abort so the
  // bracket load surfaces as "No bracket data" instead of spinning forever.
  const controller = new AbortController()
  const timeoutId  = setTimeout(() => controller.abort(), 10000)

  try {
    const res = await fetch(`${API_URL}/api/espn/${sportKey}/allplayoffs`, {
      signal: controller.signal,
    })
    const data = await res.json()
    if (!data?.events?.length) { log('[playoffs] no events'); return null }
    log('[playoffs] total events:', data.events.length)

    // Strict: recognized round abbr AND postseason season-type
    const strict = data.events.filter(e => {
      const abbr = e.competitions?.[0]?.type?.abbreviation || ''
      const st   = e.season?.type
      const slug = e.season?.slug || ''
      return PLAYOFF_ABBRS.has(abbr) && (st === 3 || st === '3' || String(slug).includes('post'))
    })
    log('[playoffs] strict filtered:', strict.length)

    // Fallback: round abbr only (season-type metadata sometimes missing)
    const toProcess = strict.length > 0
      ? strict
      : data.events.filter(e => PLAYOFF_ABBRS.has(e.competitions?.[0]?.type?.abbreviation || ''))

    const currentYear = new Date().getFullYear()
    const current = toProcess.filter(e => new Date(e.date).getFullYear() >= currentYear - 1)
    log('[playoffs] current-year events:', current.length)
    if (!current.length) return null

    return buildBracketFromGames(current, sport)
  } catch (err) {
    if (err?.name === 'AbortError') {
      console.warn('[playoffs] fetch timed out for', sportKey)
      return null
    }
    log('[playoffs] failed:', err)
    return null
  } finally {
    clearTimeout(timeoutId)
  }
}

export async function getPlayoffStatus(sport) {
  const bracket = await fetchPlayoffBracket(sport)
  if (!bracket) {
    return {
      completedRounds: [], highestComplete: 0, bracket: null,
      availableEntries: [{ id:'full', label:'Full Bracket', description:'Pick every round from the start', startRound:1, maxPoints:48, available:true }],
    }
  }

  // New bracket format: bracket.rounds is an array of { name, points, matchups }
  const completedRounds = []
  ;(bracket.rounds || []).forEach((round, idx) => {
    if (round.matchups?.length > 0 && round.matchups.every(m => m.winner)) {
      completedRounds.push(idx + 1)
    }
  })

  const highestComplete = completedRounds.length > 0 ? Math.max(...completedRounds) : 0
  const entries = []

  if (highestComplete === 0) entries.push({ id:'full',   label:'Full Bracket',      description:'Pick every round from the start',      startRound:1, maxPoints:48, available:true })
  if (highestComplete >= 1) entries.push({ id:'r2',    label:'Round 2 Bracket',   description:'Round 1 complete — pick from here',    startRound:2, maxPoints:24, available:true })
  if (highestComplete >= 2) entries.push({ id:'cf',    label:'Conference Finals',  description:'Pick Conf Finals and beyond',           startRound:3, maxPoints:16, available:true })
  if (highestComplete >= 3) entries.push({ id:'finals',label:'Finals Only',        description:'Pick the championship winner',          startRound:4, maxPoints:8,  available:true })

  if (entries.length === 0) entries.push({ id:'full', label:'Full Bracket', description:'Pick every round from the start', startRound:1, maxPoints:48, available:true })

  return { completedRounds, highestComplete, availableEntries: entries, bracket }
}

// Build A — strict two-window bracket lockout. Gates the CREATE and JOIN
// paths only; existing pools and grading are untouched. Per-sport state
// machine:
//   'pre-matchups'  → matchups not confirmed (TBD teams) or no bracket data
//   'window1'       → R1 matchups confirmed AND R1 hasn't tipped off  (OPEN)
//   'r1-active'     → R1 has started but isn't complete
//   'window2'       → R1 complete AND R2 hasn't tipped off            (OPEN)
//   'closed'        → R2 has started (or no R2 to start, defensively)
//
// Fail-OPEN discipline (mirrors Stage 6 grader's per-round-fetch-failure
// rule): transient ESPN errors / missing date fields / unexpected shape
// → return locked: false so a flaky data fetch can't block a user from
// creating or joining a pool. The lockout is a soft UX gate, not a
// security boundary — server-side guard is deferred (snapshot §5b).
export async function getBracketLockout(sport) {
  try {
    const bracket = await fetchPlayoffBracket(sport)
    if (!bracket?.rounds?.length) {
      return { locked: true, reason: `${sport} playoffs aren't active yet`, window: 'pre-matchups' }
    }

    const r1 = bracket.rounds[0]
    const r2 = bracket.rounds[1] ?? null

    const r1MatchupsConfirmed = r1.matchups?.length > 0
      && r1.matchups.every(m => m.team1 !== 'TBD' && m.team2 !== 'TBD')
    if (!r1MatchupsConfirmed) {
      return { locked: true, reason: 'Bracket opens when Round 1 matchups are confirmed', window: 'pre-matchups' }
    }

    // Earliest scheduled R1 game across all R1 matchups. Filter null/undefined
    // defensively — placeholder() doesn't set firstGameDate, and ESPN data
    // could drop the field. If we have matchups but NO dates, fall through
    // to OPEN (Window 1) so a data hiccup doesn't block the user. Log the
    // anomaly so it surfaces in Railway logs without becoming a UI error.
    const r1Dates = r1.matchups.map(m => m.firstGameDate).filter(d => d)
    if (r1Dates.length === 0) {
      console.warn('[bracket-lockout]', sport, 'R1 matchups confirmed but no firstGameDate fields — failing OPEN to window1')
      return { locked: false, reason: '', window: 'window1' }
    }
    const r1FirstStart = r1Dates.reduce((min, d) => d < min ? d : min)
    const now          = Date.now()
    const r1Started    = now >= new Date(r1FirstStart).getTime()

    if (!r1Started) {
      return { locked: false, reason: '', window: 'window1' }
    }

    const r1Complete = r1.matchups.every(m => m.winner)
    if (!r1Complete) {
      return { locked: true, reason: 'Round 1 is in progress — Bracket re-opens after Round 1 completes', window: 'r1-active' }
    }

    // R1 done. Need R2 to enter Window 2 — defensive null check covers a
    // theoretical "bracket has only R1 data" scenario that shouldn't occur
    // for real playoffs but guards against ESPN partial-publish drift.
    if (!r2 || !r2.matchups?.length) {
      return { locked: true, reason: 'Bracket closed for this playoff', window: 'closed' }
    }

    const r2Dates = r2.matchups.map(m => m.firstGameDate).filter(d => d)
    if (r2Dates.length === 0) {
      console.warn('[bracket-lockout]', sport, 'R2 matchups confirmed but no firstGameDate fields — failing OPEN to window2')
      return { locked: false, reason: '', window: 'window2' }
    }
    const r2FirstStart = r2Dates.reduce((min, d) => d < min ? d : min)
    const r2Started    = now >= new Date(r2FirstStart).getTime()

    if (!r2Started) {
      return { locked: false, reason: '', window: 'window2' }
    }

    return { locked: true, reason: 'Bracket closed for this playoff', window: 'closed' }
  } catch (err) {
    // Transient ESPN failure / network blip. Fail OPEN so the user can
    // proceed; this is consistent with how fetchPlayoffBracket itself
    // already swallows abort errors and returns null. A real data-side
    // problem will surface as the 'pre-matchups' case via the null bracket
    // path above; a network blip shouldn't user-facing-block creation.
    console.error('[bracket-lockout]', sport, 'failed — failing OPEN:', err?.message || err)
    return { locked: false, reason: '', window: 'window1' }
  }
}

export async function fetchNBAPlayoffBracket() { return fetchPlayoffBracket('NBA') }
export async function fetchNHLPlayoffBracket() { return fetchPlayoffBracket('NHL') }
export async function fetchMLBPlayoffBracket() { return fetchPlayoffBracket('MLB') }
export async function fetchNFLPlayoffBracket() { return fetchPlayoffBracket('NFL') }

export async function getActiveRound(sport) {
  const bracket = await fetchPlayoffBracket(sport)
  if (!bracket?.rounds?.length) return null
  // Return the roundOrder of the first round that has unfinished series
  for (const round of bracket.rounds) {
    if (round.matchups?.some(m => !m.winner)) return round.matchups[0]?.roundOrder ?? null
  }
  return null
}
